import React, { useCallback, useEffect, useMemo, useState } from "react";
import OrganizationApi from "../../../../../api/organization-api";
import PaymentsApi from "../../../../../api/payments-api";
import Card from "../../../../../shared/components/dashboard-layout/components/card/cardComponent";

const SubscriptionsTab = ({ organizationId, setSuccessMessage }) => {
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState("");
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [subscriptionDetailsLoading, setSubscriptionDetailsLoading] = useState(false);
  const [subscriptionDetailsError, setSubscriptionDetailsError] = useState("");
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  const [refreshSubscriptionsLoading, setRefreshSubscriptionsLoading] = useState(false);

  const billingCustomerId = useMemo(
    () => organizationDetails?.paymentInfo?.customerId || "",
    [organizationDetails]
  );

  const hasServiceAccessUntil = useMemo(
    () => organizationDetails?.hasServiceAccessUntil || "",
    [organizationDetails]
  );

  const hasBillingSubscription = useMemo(() => {
    return Boolean(billingCustomerId && hasServiceAccessUntil);
  }, [billingCustomerId, hasServiceAccessUntil]);

  const subscriptionSummary = useMemo(() => {
    const sub = subscriptionDetails && typeof subscriptionDetails === "object" ? subscriptionDetails : null;
    if (!sub) return null;

    const firstItem = sub?.items?.data?.[0];
    const price = firstItem?.price;
    const recurring = price?.recurring;

    return {
      id: sub?.id,
      status: sub?.status,
      customer: sub?.customer,
      currency: sub?.currency,
      created: sub?.created,
      startDate: sub?.start_date,
      currentPeriodStart: sub?.current_period_start,
      currentPeriodEnd: sub?.current_period_end,
      trialStart: sub?.trial_start,
      trialEnd: sub?.trial_end,
      cancelAtPeriodEnd: sub?.cancel_at_period_end,
      cancelAt: sub?.cancel_at,
      canceledAt: sub?.canceled_at,
      cancellationReason: sub?.cancellation_details?.reason,
      cancellationFeedback: sub?.cancellation_details?.feedback,
      interval: recurring?.interval || firstItem?.plan?.interval,
      intervalCount: recurring?.interval_count || firstItem?.plan?.interval_count,
      unitAmount: price?.unit_amount ?? firstItem?.plan?.amount,
      quantity: firstItem?.quantity,
    };
  }, [subscriptionDetails]);

  const formatUnixSeconds = useCallback((value) => {
    if (!value) return "—";
    const tsMs = Number(value) * 1000;
    if (Number.isNaN(tsMs)) return "—";
    const d = new Date(tsMs);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  }, []);

  const formatMoney = useCallback((amountMinor, currency) => {
    if (typeof amountMinor !== "number") return "—";
    const c = (currency || "").toUpperCase();
    const amountMajor = amountMinor / 100;
    if (!c) return String(amountMajor);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: c,
      }).format(amountMajor);
    } catch {
      return `${amountMajor.toFixed(2)} ${c}`;
    }
  }, []);

  const isServiceAccessExpired = useMemo(() => {
    if (!hasServiceAccessUntil) return false;
    const ts = new Date(hasServiceAccessUntil).getTime();
    if (Number.isNaN(ts)) return false;
    return ts < Date.now();
  }, [hasServiceAccessUntil]);

  const refreshSubscriptionsData = useCallback(async () => {
    setSubscriptionsError("");
    setSubscriptionDetailsError("");
    setRefreshSubscriptionsLoading(true);

    try {
      if (!organizationId) {
        setSubscriptionsError("Missing organizationId.");
        return;
      }

      const orgResponse = await OrganizationApi.private.fetchOrganization(organizationId);
      const orgData = orgResponse?.data?.data;
      setOrganizationDetails(orgData || null);

      const billingCustomerIdValue = orgData?.paymentInfo?.customerId;
      if (!billingCustomerIdValue) {
        setSubscriptionDetails(null);
        setSubscriptionDetailsError("");
        setSubscriptionDetailsLoading(false);
        return;
      }

      setSubscriptionDetailsLoading(true);
      setSubscriptionDetailsError("");
      try {
        const response = await PaymentsApi.private.getSubscription(organizationId);
        const data = response?.data?.data || response?.data;
        setSubscriptionDetails(data ?? null);
      } catch (error) {
        console.error("Error fetching subscription details:", error);
        setSubscriptionDetails(null);
        setSubscriptionDetailsError("Failed to load subscription details.");
      } finally {
        setSubscriptionDetailsLoading(false);
      }
    } catch (error) {
      console.error("Error refreshing subscription data:", error);
      setSubscriptionsError("Failed to refresh subscription data.");
    } finally {
      setRefreshSubscriptionsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    refreshSubscriptionsData();
  }, [refreshSubscriptionsData]);

  const startSubscriptionCheckout = async () => {
    setSubscriptionsError("");
    setSubscriptionsLoading(true);

    try {
      const response = await PaymentsApi.private.createCheckoutSession(organizationId);

      const redirectUrl =
        response?.data?.url ||
        response?.data?.data?.url ||
        response?.data?.checkoutUrl ||
        response?.data?.data?.checkoutUrl ||
        response?.data?.checkout_url ||
        response?.data?.data?.checkout_url;

      if (!redirectUrl) {
        setSubscriptionsError("Missing checkout URL from checkout-session response.");
        return;
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      console.error("Subscription checkout session failed:", error);
      setSubscriptionsError("Failed to start checkout. Please try again.");
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const openSubscriptionBillingPortal = async () => {
    setSubscriptionsError("");
    setSubscriptionsLoading(true);

    try {
      const response = await PaymentsApi.private.createBillingPortalSession(organizationId);

      const redirectUrl =
        response?.data?.url ||
        response?.data?.data?.url ||
        response?.data?.checkoutUrl ||
        response?.data?.data?.checkoutUrl ||
        response?.data?.checkout_url ||
        response?.data?.data?.checkout_url;

      if (!redirectUrl) {
        setSubscriptionsError("Missing checkout URL from billing-portal-session response.");
        return;
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      console.error("Subscription billing portal session failed:", error);
      setSubscriptionsError("Failed to open billing portal. Please try again.");
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  return (
    <Card
      header="Subscriptions"
      subheader="Manage your subscription"
      notes=""
    >
        {isServiceAccessExpired ? (
          <div className="alert alert-warning">
            Your service access has expired. Please start checkout to renew access.
          </div>
        ) : null}

        {subscriptionsError ? <div className="alert alert-danger">{subscriptionsError}</div> : null}

        {subscriptionDetailsLoading ? <div className="text-muted">Loading subscription summary…</div> : null}
        {subscriptionDetailsError ? <div className="alert alert-danger">{subscriptionDetailsError}</div> : null}

        {!subscriptionDetailsLoading && !subscriptionDetailsError && !billingCustomerId ? (
          <div className="text-muted">You don't have a subscription.</div>
        ) : null}

        {!subscriptionDetailsLoading && !subscriptionDetailsError ? (
          subscriptionSummary ? (
            <div className="row">
              {/* <div className="col-md-6 mb-3">
                <strong>Subscription ID:</strong>
                <div>{subscriptionSummary.id || "—"}</div>
              </div> */}
              <div className="col-md-6 mb-3">
                <strong>Status:</strong>
                <div>{subscriptionSummary.status || "—"}</div>
              </div>
              {/* <div className="col-md-6 mb-3">
                <strong>Customer:</strong>
                <div>{subscriptionSummary.customer || "—"}</div>
              </div> */}
              <div className="col-md-6 mb-3">
                <strong>Created:</strong>
                <div>{formatUnixSeconds(subscriptionSummary.created)}</div>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Start date:</strong>
                <div>{formatUnixSeconds(subscriptionSummary.startDate)}</div>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Amount:</strong>
                <div>
                  {formatMoney(subscriptionSummary.unitAmount, subscriptionSummary.currency)}
                  {subscriptionSummary.quantity ? ` x ${subscriptionSummary.quantity}` : ""}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Billing interval:</strong>
                <div>
                  {subscriptionSummary.interval
                    ? `Every ${subscriptionSummary.intervalCount || 1} ${subscriptionSummary.interval}`
                    : "—"}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Current period start:</strong>
                <div>{formatUnixSeconds(subscriptionSummary.currentPeriodStart)}</div>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Current period end:</strong>
                <div>{formatUnixSeconds(subscriptionSummary.currentPeriodEnd)}</div>
              </div>
              {subscriptionSummary.trialStart || subscriptionSummary.trialEnd ? (
                <>
                  <div className="col-md-6 mb-3">
                    <strong>Trial start:</strong>
                    <div>{formatUnixSeconds(subscriptionSummary.trialStart)}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Trial end:</strong>
                    <div>{formatUnixSeconds(subscriptionSummary.trialEnd)}</div>
                  </div>
                </>
              ) : null}
              {subscriptionSummary.cancelAtPeriodEnd || subscriptionSummary.cancelAt || subscriptionSummary.canceledAt ? (
                <>
                  <div className="col-md-6 mb-3">
                    <strong>Cancel at period end:</strong>
                    <div>
                      {typeof subscriptionSummary.cancelAtPeriodEnd === "boolean"
                        ? subscriptionSummary.cancelAtPeriodEnd
                          ? "Yes"
                          : "No"
                        : "—"}
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Cancel at:</strong>
                    <div>{formatUnixSeconds(subscriptionSummary.cancelAt)}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Canceled at:</strong>
                    <div>{formatUnixSeconds(subscriptionSummary.canceledAt)}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Cancellation details:</strong>
                    <div>
                      {subscriptionSummary.cancellationReason || subscriptionSummary.cancellationFeedback
                        ? [
                            subscriptionSummary.cancellationReason,
                            subscriptionSummary.cancellationFeedback,
                          ]
                            .filter(Boolean)
                            .join(" / ")
                        : "—"}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <></>
            // <div className="text-muted">No subscription details available.</div>
          )
        ) : null}

        <div className="d-flex gap-2 justify-content-end">
          <button
            className="bouncy-button"
            type="button"
            onClick={refreshSubscriptionsData}
            disabled={refreshSubscriptionsLoading}
          >
            <span>{refreshSubscriptionsLoading ? "Refreshing" : "Refresh"}</span>
          </button>

          {!hasBillingSubscription ? (
            <button
              className="bouncy-button"
              onClick={startSubscriptionCheckout}
              disabled={subscriptionsLoading}
              type="button"
            >
              <span>{subscriptionsLoading ? "Starting" : "Start subscription"}</span>
            </button>
          ) : (
            <button
              className="bouncy-button"
              type="button"
              onClick={openSubscriptionBillingPortal}
              disabled={subscriptionsLoading}
            >
              <span>{subscriptionsLoading ? "Opening" : "Manage subscription"}</span>
            </button>
          )}
        </div>
    </Card>
  );
};

export default SubscriptionsTab;
